import { notifyError } from "@/lib/notify-error";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useAction, useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useRouteParam } from "@/lib/use-route-param";
import {
  AdminActionsCard,
  AdvancedEntitlementCard,
  hasStripeSubscription,
  type AdminActionFormState,
  type AdminActionKind,
  type BillingDetail,
  type DurationChoice,
  type EntitlementFormState,
  type PendingBillingAction,
} from "@/features/admin-billing/admin-billing-actions";
import {
  EnterpriseBillingCard,
  type EnterpriseCreateInput,
  type EnterpriseUpdateInput,
} from "@/features/admin-billing/enterprise-billing-card";
import { SeatSyncStatusBadge } from "@/features/admin-billing/billing-status-badges";
import { toTitleCase } from "@/lib/formatting";
import { PageHeader, PageHeaderSkeleton } from "@/components/layout";
import { RoleBadge } from "@/components/semantic-badges";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const DAY_MS = 24 * 60 * 60 * 1000;

export const Route = createFileRoute("/admin/billing/$orgId")({
  component: AdminBillingDetailPage,
});

function formatDate(value: number | null) {
  if (!value) {
    return "-";
  }
  return new Date(value).toLocaleString();
}

function formatMode(value: string | null | undefined) {
  return value ? toTitleCase(value) : "-";
}

function addDays(value: number, days: number) {
  return value + days * DAY_MS;
}

function defaultReason(action: AdminActionKind, duration: DurationChoice) {
  const durationLabel = duration === "custom" ? "custom time" : `${duration} days`;
  switch (action) {
    case "trial":
      return `Admin granted ${durationLabel} trial`;
    case "free_time":
      return `Admin extended billing by ${durationLabel}`;
    case "free_forever":
      return "Admin granted free forever access";
  }
}

function toDateTimeLocalInput(value: number | null | undefined) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
}

function parseDateInput(value: string) {
  if (!value.trim()) {
    return undefined;
  }
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? undefined : timestamp;
}

function getDurationDays(duration: DurationChoice) {
  return duration === "custom" ? undefined : Number(duration);
}

function isExtendableSubscriptionStatus(status: string | undefined) {
  return status === "active" || status === "trialing";
}

function buildDefaultAdminAction(detail: BillingDetail): AdminActionFormState {
  const canExtendBilling =
    hasStripeSubscription(detail) &&
    isExtendableSubscriptionStatus(detail.billing?.subscriptionStatus);
  const action = canExtendBilling
    ? "free_time"
    : hasStripeSubscription(detail)
      ? "free_forever"
      : "trial";
  // Internal trials default to the policy's 14 days; other actions to 30.
  const duration: DurationChoice = action === "trial" ? "14" : "30";
  return {
    action,
    duration,
    customEndsAt: toDateTimeLocalInput(addDays(detail.summary.renewalDate ?? Date.now(), Number(duration))),
    seatChoice: "unlimited",
    seatLimit: "",
    reason: defaultReason(action, duration),
  };
}

function buildInitialForm(detail: BillingDetail): EntitlementFormState {
  return {
    planKey: detail.entitlement?.planKey ?? "manual_override",
    billingMode: (detail.entitlement?.billingMode ?? "manual_free") as EntitlementFormState["billingMode"],
    accessStatus: (detail.entitlement?.accessStatus ?? "active") as EntitlementFormState["accessStatus"],
    seatPolicy: (detail.entitlement?.seatPolicy ?? "unlimited") as EntitlementFormState["seatPolicy"],
    seatLimit: detail.entitlement?.seatLimit ? String(detail.entitlement.seatLimit) : "",
    expiresAt: toDateTimeLocalInput(detail.entitlement?.expiresAt),
    graceEndsAt: toDateTimeLocalInput(detail.entitlement?.graceEndsAt),
    reason: detail.entitlement?.reason ?? "",
  };
}

function AdminBillingDetailPage() {
  const orgId = useRouteParam("orgId");
  const { data, isPending, error } = useQuery(
    convexQuery(
      api.admin.billing.getOrganizationBillingDetail,
      orgId ? { organizationId: orgId as Id<"organizations"> } : "skip"
    )
  );
  const detail = data as BillingDetail | undefined;
  const setEntitlement = useMutation(api.admin.billing.setOrganizationEntitlement);
  const restoreProviderEntitlement = useMutation(api.admin.billing.restoreProviderEntitlement);
  const reconcileOrganizationSeats = useAction(api.admin.billing.reconcileOrganizationSeats);
  const extendProviderBillingPeriod = useAction(api.admin.billing.extendProviderBillingPeriod);
  const createEnterpriseSubscription = useAction(api.admin.billing.createEnterpriseSubscription);
  const updateEnterpriseSubscription = useAction(api.admin.billing.updateEnterpriseSubscription);
  const cancelEnterpriseSubscription = useAction(api.admin.billing.cancelEnterpriseSubscription);
  const [pendingAction, setPendingAction] = useState<PendingBillingAction>(null);
  const [form, setForm] = useState<EntitlementFormState | null>(null);
  const [adminAction, setAdminAction] = useState<AdminActionFormState | null>(null);

  useEffect(() => {
    if (detail) {
      setForm(buildInitialForm(detail));
      setAdminAction(buildDefaultAdminAction(detail));
    }
  }, [detail]);

  if (!orgId) {
    return <div className="py-24 text-center text-muted-foreground">Invalid billing route.</div>;
  }

  if (error) {
    return <div className="py-24 text-center text-muted-foreground">Failed to load billing details.</div>;
  }

  if (isPending || detail === undefined || form === null || adminAction === null) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton withTrail />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  const handleChange = <K extends keyof EntitlementFormState>(
    key: K,
    value: EntitlementFormState[K]
  ) => {
    setForm((current) => (current ? { ...current, [key]: value } : current));
  };

  const handleAdminActionChange = <K extends keyof AdminActionFormState>(
    key: K,
    value: AdminActionFormState[K]
  ) => {
    setAdminAction((current) => {
      if (!current) {
        return current;
      }
      const next = { ...current, [key]: value };
      if (key === "duration" && current.reason === defaultReason(current.action, current.duration)) {
        next.reason = defaultReason(current.action, value as DurationChoice);
      }
      return next;
    });
  };

  const handleSelectAdminAction = (action: AdminActionKind) => {
    setAdminAction((current) => {
      const base = current ?? buildDefaultAdminAction(detail);
      const duration: DurationChoice = action === "trial" && base.duration !== "custom" ? "14" : base.duration;
      return {
        ...base,
        action,
        duration,
        reason: defaultReason(action, duration),
      };
    });
  };

  const getSeatGrant = () => {
    if (adminAction.seatChoice === "unlimited") {
      return { seatPolicy: "unlimited" as const, seatLimit: undefined };
    }
    const seatLimit = Number(adminAction.seatLimit);
    if (!Number.isInteger(seatLimit) || seatLimit <= 0) {
      throw new Error("Enter a positive whole number of seats");
    }
    return { seatPolicy: "fixed_limit" as const, seatLimit };
  };

  const getManualGrantExpiration = () => {
    const durationDays = getDurationDays(adminAction.duration);
    if (durationDays !== undefined) {
      return addDays(Date.now(), durationDays);
    }
    const expiresAt = parseDateInput(adminAction.customEndsAt);
    if (!expiresAt) {
      throw new Error("Choose when the grant should end");
    }
    if (expiresAt <= Date.now()) {
      throw new Error("Grant end date must be in the future");
    }
    return expiresAt;
  };

  const handleApplyAdminAction = async () => {
    setPendingAction("adminAction");
    try {
      const reason = adminAction.reason.trim() || defaultReason(adminAction.action, adminAction.duration);
      if (adminAction.action === "free_time") {
        if (!hasStripeSubscription(detail)) {
          throw new Error("Free time requires an active Stripe subscription. Use a trial for orgs without billing.");
        }
        if (!isExtendableSubscriptionStatus(detail.billing?.subscriptionStatus)) {
          throw new Error("Free time requires an active Stripe subscription.");
        }
        const durationDays = getDurationDays(adminAction.duration);
        const newPeriodEnd = durationDays === undefined ? parseDateInput(adminAction.customEndsAt) : undefined;
        if (durationDays === undefined && !newPeriodEnd) {
          throw new Error("Choose the new billing date");
        }
        await extendProviderBillingPeriod({
          organizationId: orgId as Id<"organizations">,
          durationDays,
          newPeriodEnd,
          reason,
        });
        toast.success("Stripe billing period extended");
        return;
      }

      const seatGrant = getSeatGrant();
      await setEntitlement({
        organizationId: orgId as Id<"organizations">,
        planKey: adminAction.action === "trial" ? "trial" : "manual_free",
        billingMode: adminAction.action === "trial" ? "trial" : "internal",
        accessStatus: "active",
        seatPolicy: seatGrant.seatPolicy,
        seatLimit: seatGrant.seatLimit,
        expiresAt: adminAction.action === "trial" ? getManualGrantExpiration() : undefined,
        reason,
      });
      toast.success(adminAction.action === "trial" ? "Free trial started" : "Free forever access granted");
    } catch (actionError) {
      notifyError(actionError, actionError instanceof Error ? actionError.message : "Failed to apply billing action");
    } finally {
      setPendingAction(null);
    }
  };

  const handleSave = async () => {
    setPendingAction("save");
    try {
      await setEntitlement({
        organizationId: orgId as Id<"organizations">,
        planKey: form.planKey,
        billingMode: form.billingMode,
        accessStatus: form.accessStatus,
        seatPolicy: form.seatPolicy,
        seatLimit: form.seatLimit.trim() ? Number(form.seatLimit) : undefined,
        expiresAt: parseDateInput(form.expiresAt),
        graceEndsAt: parseDateInput(form.graceEndsAt),
        reason: form.reason.trim() || "Manual entitlement update",
      });
      toast.success("Entitlement updated");
    } catch (saveError) {
      notifyError(saveError, saveError instanceof Error ? saveError.message : "Failed to update entitlement");
    } finally {
      setPendingAction(null);
    }
  };

  const handleRestoreProvider = async () => {
    setPendingAction("restore");
    try {
      await restoreProviderEntitlement({
        organizationId: orgId as Id<"organizations">,
        reason: "Restore Stripe-managed entitlement",
      });
      toast.success("Stripe entitlement restored");
    } catch (restoreError) {
      notifyError(restoreError,
        restoreError instanceof Error ? restoreError.message : "Failed to restore Stripe entitlement"
      );
    } finally {
      setPendingAction(null);
    }
  };

  const runEnterpriseAction = async (
    kind: "enterpriseCreate" | "enterpriseUpdate" | "enterpriseCancel",
    run: () => Promise<unknown>,
    messages: { success: string; failure: string }
  ): Promise<boolean> => {
    setPendingAction(kind);
    try {
      await run();
      toast.success(messages.success);
      return true;
    } catch (enterpriseError) {
      notifyError(enterpriseError, enterpriseError instanceof Error ? enterpriseError.message : messages.failure);
      return false;
    } finally {
      setPendingAction(null);
    }
  };

  const handleCreateEnterprise = (input: EnterpriseCreateInput) =>
    runEnterpriseAction(
      "enterpriseCreate",
      () => createEnterpriseSubscription({ organizationId: orgId as Id<"organizations">, ...input }),
      { success: "Enterprise invoicing started", failure: "Failed to start enterprise invoicing" }
    );

  const handleUpdateEnterprise = (input: EnterpriseUpdateInput) =>
    runEnterpriseAction(
      "enterpriseUpdate",
      () => updateEnterpriseSubscription({ organizationId: orgId as Id<"organizations">, ...input }),
      { success: "Enterprise contract updated", failure: "Failed to update the enterprise contract" }
    );

  const handleCancelEnterprise = (atPeriodEnd: boolean) =>
    runEnterpriseAction(
      "enterpriseCancel",
      () => cancelEnterpriseSubscription({
        organizationId: orgId as Id<"organizations">,
        atPeriodEnd,
        reason: atPeriodEnd ? "Enterprise contract ends at period end" : "Enterprise contract canceled",
      }),
      {
        success: atPeriodEnd ? "Subscription will end at period end" : "Subscription canceled",
        failure: "Failed to cancel the subscription",
      }
    );

  const handleReconcile = async () => {
    setPendingAction("reconcile");
    try {
      const result = await reconcileOrganizationSeats({
        organizationId: orgId as Id<"organizations">,
      });
      if (result.synced) {
        toast.success(`Reconciled ${result.seatCount} seats`);
      } else {
        toast.error(result.reason ?? "No Stripe subscription found");
      }
    } catch (reconcileError) {
      notifyError(reconcileError,
        reconcileError instanceof Error ? reconcileError.message : "Failed to reconcile seats"
      );
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={detail.organization.name}
        trail={[{ label: "Billing", href: "/admin/billing" }]}
      />

      <div className="grid gap-4 md:grid-cols-4">
        <SummaryStat label="Billing mode" value={formatMode(detail.summary.billingMode)} />
        <SummaryStat label="Access" value={formatMode(detail.summary.accessStatus)} />
        <SummaryStat label="Billed vs paid seats" value={`${detail.summary.billedSeats ?? 0} / ${detail.summary.billableSeats}`} />
        <SummaryStat label="Seat status" value={formatMode(detail.summary.seatSyncStatus)} />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <BillingStateCard detail={detail} />
        <Card className="py-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Reconciliation</CardTitle>
            <CardDescription>
              Self-serve seat counts follow active owners, admins, and members in the app. Reviewers are free. Enterprise
              contracts bill their contracted seat count.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Current seat delta: {detail.summary.seatDelta ?? 0}. All users keep access while reconciliation is pending.
            </p>
            {detail.summary.seatSyncFailure ? (
              <p className="text-sm text-destructive">
                Last {detail.summary.seatSyncFailure.failedAttempts} seat update
                {detail.summary.seatSyncFailure.failedAttempts === 1 ? "" : "s"} failed
                {detail.summary.seatSyncFailure.lastFailureAt
                  ? ` (latest ${formatDate(detail.summary.seatSyncFailure.lastFailureAt)})`
                  : ""}
                : {detail.summary.seatSyncFailure.lastError ?? "Unknown error"}.{" "}
                {detail.summary.seatSyncFailure.nextRetryAt
                  ? `Next automatic retry ${formatDate(detail.summary.seatSyncFailure.nextRetryAt)}.`
                  : "Automatic retries are exhausted; the daily reconcile keeps trying."}
              </p>
            ) : null}
            <Button onClick={() => void handleReconcile()} disabled={pendingAction !== null}>
              {pendingAction === "reconcile" && <Loader2 className="size-4 animate-spin" />}
              Reconcile seats
            </Button>
          </CardContent>
        </Card>
      </div>

      <AdminActionsCard
        detail={detail}
        form={adminAction}
        pendingAction={pendingAction}
        onSelectAction={handleSelectAdminAction}
        onChange={handleAdminActionChange}
        onApply={() => void handleApplyAdminAction()}
        onRestoreProvider={() => void handleRestoreProvider()}
      />

      <EnterpriseBillingCard
        key={`${detail.billing?.providerSubscriptionId ?? "none"}:${detail.billing?.subscriptionStatus ?? ""}`}
        detail={detail}
        pendingAction={pendingAction}
        onCreate={handleCreateEnterprise}
        onUpdate={handleUpdateEnterprise}
        onCancel={handleCancelEnterprise}
      />

      <AdvancedEntitlementCard
        form={form}
        pendingAction={pendingAction}
        onChange={handleChange}
        onSave={() => void handleSave()}
        onRestoreProvider={() => void handleRestoreProvider()}
        canRestoreProvider={hasStripeSubscription(detail)}
      />

      <div className="grid gap-6 xl:grid-cols-2">
        <MembersCard members={detail.activeMembers} />
        <WebhookEventsCard events={detail.webhookEvents} />
      </div>
    </div>
  );
}

function SummaryStat(props: { label: string; value: string }) {
  return (
    <div className="rounded-lg border-2 border-border bg-card px-6 py-4">
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {props.label}
      </p>
      <p className="mt-1 text-2xl font-bold tracking-tight text-foreground">{props.value}</p>
    </div>
  );
}

function BillingStateCard(props: { detail: BillingDetail }) {
  const { detail } = props;
  return (
    <Card className="py-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Billing State</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <InfoRow label="Provider" value={detail.summary.provider ?? "-"} />
        <InfoRow label="Billing status" value={formatMode(detail.summary.billingStatus)} />
        <InfoRow label="Plan key" value={detail.summary.planKey ?? "-"} />
        <InfoRow label="Effective source" value={formatMode(detail.summary.accessSource)} />
        <InfoRow label="Entitlement status" value={formatMode(detail.summary.entitlementLifecycleStatus)} />
        <InfoRow label="Entitlement expiry" value={formatDate(detail.summary.expiresAt)} />
        <InfoRow label="Seat policy" value={formatMode(detail.summary.seatPolicy)} />
        <InfoRow label="Seat limit" value={detail.summary.seatLimit === null ? "-" : String(detail.summary.seatLimit)} />
        <InfoRow label="Renewal" value={formatDate(detail.summary.renewalDate)} />
        <InfoRow label="Last webhook" value={formatDate(detail.summary.lastWebhookAt)} />
        <InfoRow label="Last seat sync" value={formatDate(detail.summary.lastSeatSyncAt)} />
        <InfoRow label="Reason" value={detail.summary.reason ?? "-"} />
        {detail.summary.duplicateSubscriptionIds.length > 0 ? (
          <p className="text-sm font-medium text-destructive">
            Extra live Stripe subscriptions: {detail.summary.duplicateSubscriptionIds.join(", ")}. Cancel them in
            Stripe; access follows {detail.billing?.providerSubscriptionId ?? "the original subscription"}.
          </p>
        ) : null}
        {detail.summary.requiresReconciliation ? (
          <StatusBadge status="error" label="Needs Reconciliation" />
        ) : (
          <SeatSyncStatusBadge status={detail.summary.seatSyncStatus} />
        )}
      </CardContent>
    </Card>
  );
}

function InfoRow(props: { label: string; value: string }) {
  return (
    <p>
      <span className="text-muted-foreground">{props.label}:</span> {props.value}
    </p>
  );
}

function MembersCard(props: {
  members: Array<{ _id: string; email: string; name: string | null; orgRole: string }>;
}) {
  return (
    <Card className="py-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Active Members</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {props.members.map((member) => (
                <TableRow key={member._id}>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>{member.name ?? "-"}</TableCell>
                  <TableCell>
                    <RoleBadge role={member.orgRole} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function WebhookEventsCard(props: {
  events: Array<{ _id: string; eventType: string; occurredAt: number; processedAt: number }>;
}) {
  return (
    <Card className="py-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Recent Webhooks</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Occurred</TableHead>
                <TableHead>Processed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {props.events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                    No webhook events yet.
                  </TableCell>
                </TableRow>
              ) : (
                props.events.map((event) => (
                  <TableRow key={event._id}>
                    <TableCell>{event.eventType}</TableCell>
                    <TableCell>{formatDate(event.occurredAt)}</TableCell>
                    <TableCell>{formatDate(event.processedAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
